import { UserService } from '@shared/services/user.service';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BasePanelEvent, BasePanelService } from '@modules/base-panel/pages/base-panel/services/base-panel.service';
import { RouteData } from '@shared/models/route-data.model';
import { AbstractSubscriptionComponent } from '@shared/abstracts/subscription.component.abstract';
import { XPartnerService } from '@shared/services/x-partner.service';
import { StatementService } from '@modules/statement/shared/services/statement.service';
import { OnboardingService } from '@shared/services/onboarding.service';
import { ActiveSubscriptionService } from '@shared/services/active-subscription.service';
import { SemrushService } from '@shared/services/semrush.service';
import { sleep } from '@shared/helpers/sleep.helper';
import { filter, fromEvent, interval } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'df-base-panel',
  templateUrl: './base-panel.component.html',
  styleUrls: ['./base-panel.component.scss'],
})
export class BasePanelComponent extends AbstractSubscriptionComponent implements OnInit, AfterViewInit, OnDestroy {
  closed = true;
  visibleNovuNotification = false;

  @ViewChild('basePanelContent')
  private basePanelContent!: ElementRef<HTMLDivElement>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private changes: ChangeDetectorRef,
    public basePanelService: BasePanelService,
    public xPartnerService: XPartnerService,
    private userService: UserService,
    private statementService: StatementService,
    public onboardingService: OnboardingService,
    private readonly activeSubscriptionService: ActiveSubscriptionService,
    private semrushService: SemrushService,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.checkOnboarding();
    this.foundSnapshotData();
    this.listenRouterData();
    this.listenOpenGetResponseChat();

    this.basePanelService.novuNotoficationsSubject.subscribe({
      next: (val) => (this.visibleNovuNotification = val),
    });

    if (this.userService.User?.isSemrushPlan()) {
      await sleep(500);

      interval(3000).subscribe(async () => {
        await this.semrushService.getAccessToken();
      });
    }
  }

  ngAfterViewInit(): void {
    this.basePanelService.setScrollListener(fromEvent(this.basePanelContent.nativeElement, 'scroll'));
  }

  private checkOnboarding(): void {
    if (this.activeSubscriptionService.getNoActiveSubscription) return;
    if (!this.onboardingService.onboardingCompleted) {
      this.onboardingService.initOnboarding();
      this.onboardingService.nextStep$.pipe(filter((step) => !step)).subscribe(() => this.checkStatement());
    } else {
      this.checkStatement();
    }
  }

  private checkStatement(): void {
    if (
      this.onboardingService.onboardingCompleted &&
      this.statementService.openStatementOnboarding &&
      !!this.userService.User &&
      !this.userService.User!.filledStatement
    ) {
      this.router.navigate(['', { outlets: { statement: 'statement/1' } }]);
    }
  }

  private foundSnapshotData() {
    const found = (firstChild?: { data: any; firstChild?: any }) => {
      if (firstChild && firstChild?.data.metaTitle) {
        return firstChild?.data;
      }
      if (firstChild && firstChild.firstChild) {
        return found(firstChild.firstChild);
      }
      return null;
    };
    this.setDataToBasePanel(found(this.route.snapshot));
  }

  private listenRouterData() {
    this.sub.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map(() => this.route),
          map((route) => {
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route;
          }),
          mergeMap((route) => route.data),
        )
        .subscribe((routeData) => {
          this.setDataToBasePanel(routeData as RouteData);
          this.changes.detectChanges();
        }),
    );
  }

  setDataToBasePanel(data: RouteData) {
    if (data) {
      this.basePanelService.routeData = data;
      // full left menu for the newly registered users (till the first toggling menu by the user)
      const shortLeftNavigationValue =
        this.userService.isNewUserRegistered() && !this.onboardingService.onboardingRunning
          ? false
          : data.shortLeftNavigation!;
      this.basePanelService.setShortLeftNavigation(shortLeftNavigationValue);
    }
  }

  private listenOpenGetResponseChat() {
    this.sub.add(
      this.basePanelService.emitter.subscribe((e: BasePanelEvent) => {
        switch (e) {
          case BasePanelEvent.OPEN_CHAT:
            this.closed = false;
            break;
        }
      }),
    );
  }

  closeChat(isClosed: boolean) {
    this.closed = isClosed;
  }
}
